// A bare `<v-chart>` line chart — no card, like BaseDonutChart and BaseBarChart:
// the page owns the card, the title and any metric toggle. One line series over a
// category axis, with an optional gradient area under it.
//
// It replaces the app's `CountCreatedLineChart.vue`, which aggregated its own
// cumulative rows, carried its line colour and both gradient stops as literals, and
// leaked a hand-rolled window resize listener. The aggregation moved to the app
// (`composables/useDocumentCharts.js`), the colour to `@rdrive/theme`
// (`chart.series[colorIndex]`, with the gradient derived from it by `withAlpha`),
// and the resize to `<v-chart autoresize>`.
//
//   props:  rows        (Array)    [{ label, value }] in x order — the page pre-sorts
//                                  and pre-accumulates; "cumulative" is its business
//           seriesName  (String)   tooltip line label — already translated
//           formatValue (Function) (value) => string; tooltip + value-axis labels
//           area        (Boolean)  gradient area under the line (0.5 → 0.1 alpha)
//           smooth      (Boolean)  smoothed line
//           colorIndex  (Number)   index into chart.series
//           labelRotate (Number)   category-axis label rotation
//           height      (Number)   chart height in px
//   opt-in (AM_ABSORPTION_PLAN step E4; every one off by default — the option is unchanged):
//           row.value null           a gap in the line (ECharts' own null) — bridged with connectNulls
//           color       (String)   the line's colour (a @rdrive/theme hex), over colorIndex; the area
//                                  gradient is derived from it
//           connectNulls (Boolean) draw the line across null values instead of breaking it
//           symbolSize  (Number)   the point markers' size in px
//           valueMin / valueMax (Number) the value axis' min / max (e.g. 0 / 100 for a percentage)
//           formatAxisValue (Function) (value) => string; the value-axis labels only (over formatValue,
//                                  which keeps the tooltip) — '83%' on the axis, '82.7%' in the tooltip
//   opt-in (2026-10-16, PNG export on every chart; off by default — nothing rendered changes):
//           exportFilename (String) the PNG's file name; '' = `downloadPng()` does nothing
//           exportTitle    (String) the PNG's title ('' = none: a bare chart has no title of its own)
//   setup:  downloadPng()  saves the chart as a PNG (no legend) — a bare chart renders no button:
//                          its page owns the card and calls this through a template ref
//
// No emits, no strings and no `messages/`, and no `chartFontColor` prop: colours
// come from `useChartTheme()`.
//
// Full contract: ../../../README.md → BaseLineChart. The logic lives in ./setup.js.
//
// The echarts module registration is wiring for the `VChart` registration below —
// both are what the template's `<v-chart>` needs — so it lives here and ./setup.js
// stays pure logic. The SFC this component replaces also registered `TitleComponent`
// and `LegendComponent`; nothing in the option ever used a title or a legend, so
// they are dropped.
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import { TooltipComponent, GridComponent } from "echarts/components";
import VChart from "vue-echarts";
import setup from './setup.js';

use([CanvasRenderer, LineChart, TooltipComponent, GridComponent]);

export default {
  name: 'base-line-chart',
  props: {
    // [{ label, value }] in x order. Reactive BY REFERENCE: the adapter hands over a
    // new array on every change, so there is no deep watch here.
    rows: {
      type: Array,
      required: true
    },
    // Tooltip line label — already translated by the page
    seriesName: {
      type: String,
      default: ''
    },
    // (value) => string, for the tooltip and the value-axis labels
    formatValue: {
      type: Function,
      default: (value) => String(value)
    },
    // Gradient area under the line, from the series colour at 0.5 → 0.1 alpha
    area: {
      type: Boolean,
      default: true
    },
    smooth: {
      type: Boolean,
      default: true
    },
    colorIndex: {
      type: Number,
      default: 0
    },
    labelRotate: {
      type: Number,
      default: 45
    },
    height: {
      type: Number,
      default: 400
    },
    // --- Opt-in (AM_ABSORPTION_PLAN step E4, asset-management's Pass Rate Trend — a monthly average
    // on a 0–100 % axis with months that have no inspection). Each one defaults off, and off adds
    // nothing to the option: project-overview's uploads line is byte-for-byte what it was.
    // The line's colour — a hex from @rdrive/theme (`useChartTheme()`), never a literal — over
    // `colorIndex`; with `area` on, the gradient is derived from it (`withAlpha` needs a hex).
    // null = chart.series[colorIndex]
    color: {
      type: String,
      default: null
    },
    // Draw the line across a row whose `value` is null (a month without data) instead of breaking it
    connectNulls: {
      type: Boolean,
      default: false
    },
    // The point markers' size in px; null = ECharts' default
    symbolSize: {
      type: Number,
      default: null
    },
    // The value axis' min / max (0 / 100 for a percentage); null = ECharts scales the axis
    valueMin: {
      type: Number,
      default: null
    },
    valueMax: {
      type: Number,
      default: null
    },
    // (value) => string for the value-axis labels only; null = formatValue (which keeps the tooltip)
    formatAxisValue: {
      type: Function,
      default: null
    },
    // --- Opt-in (2026-10-16, PNG export on every chart). No button here — the page owns the card
    // and its title row, and calls `downloadPng()` through a template ref.
    // The PNG's file name; '' = export off (`downloadPng()` is a no-op)
    exportFilename: {
      type: String,
      default: ''
    },
    // The PNG's title (the page's card title); '' = no title band
    exportTitle: {
      type: String,
      default: ''
    }
  },
  // `<v-chart>` was auto-registered from the import by `<script setup>`; a classic
  // component has to register it explicitly or Vue logs "Failed to resolve component:
  // v-chart" and renders nothing. Vuetify tags need no entry — vite-plugin-vuetify
  // rewrites the compiled render code, not the script block.
  components: { VChart },
  setup,
};
